/**
 * Typed client for the Today + Pipeline module routes (`tpRoutes` in @bs/contracts). Same rules as api/client.ts:
 * cookie session, CSRF header on writes, request body validated before sending, response validated against the
 * contract, every failure becomes an ApiError (never swallowed).
 */
import type { z } from 'zod';
import { API_PREFIX, ErrorEnvelope, tpRoutes, type TpRouteName } from '@bs/contracts';
import { api, UNAUTH_EVENT } from '../../api/instance.js';
import { ApiError, buildPath } from '../../api/client.js';

type R = typeof tpRoutes;
type BodyOf<K extends TpRouteName> = R[K] extends { body: z.ZodTypeAny } ? z.input<R[K]['body']> : undefined;
type QueryOf<K extends TpRouteName> = R[K] extends { query: z.ZodTypeAny } ? Partial<z.input<R[K]['query']>> : undefined;
export type TpResponse<K extends TpRouteName> = z.output<R[K]['response']>;

export interface TpCall<K extends TpRouteName> {
  params?: Record<string, string>;
  query?: QueryOf<K>;
  body?: BodyOf<K>;
}

/** Overridable for tests. */
export const tpConfig: { baseUrl: string; fetch: typeof fetch } = {
  baseUrl: (import.meta.env.VITE_API_URL as string | undefined) ?? '',
  fetch: (...a: Parameters<typeof fetch>) => fetch(...a),
};

export async function tp<K extends TpRouteName>(name: K, opts: TpCall<K> = {}): Promise<TpResponse<K>> {
  const route = tpRoutes[name] as { method: string; path: string; body?: z.ZodTypeAny; response: z.ZodTypeAny };
  let url = tpConfig.baseUrl.replace(/\/$/, '') + API_PREFIX + buildPath(route.path, opts.params);
  if (opts.query) {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query as Record<string, unknown>)) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
    const s = qs.toString();
    if (s) url += `?${s}`;
  }
  const headers: Record<string, string> = { Accept: 'application/json' };
  let body: string | undefined;
  if (route.body) {
    const parsed = route.body.safeParse(opts.body ?? {});
    if (!parsed.success) {
      throw new ApiError(0, 'VALIDATION_FAILED', parsed.error.issues[0]?.message ?? 'Please check the highlighted fields.', undefined,
        parsed.error.issues.map((i) => ({ path: i.path.join('.'), message: i.message })));
    }
    body = JSON.stringify(parsed.data);
    headers['Content-Type'] = 'application/json';
  }
  const csrf = api.getCsrfToken();
  if (route.method !== 'GET' && csrf) headers['X-CSRF-Token'] = csrf;
  let res: Response;
  try {
    res = await tpConfig.fetch(url, { method: route.method, headers, body, credentials: 'include' });
  } catch (e) {
    console.warn('tp fetch failed', e);
    throw new ApiError(0, 'NETWORK', 'Cannot reach the server. Check your connection and try again.');
  }
  if (!res.ok) {
    if (res.status === 401) window.dispatchEvent(new Event(UNAUTH_EVENT));
    const text = await res.text();
    let raw: unknown = null;
    if (text) {
      try { raw = JSON.parse(text); } catch (e) { console.warn('error body is not JSON', e); }
    }
    const env = ErrorEnvelope.safeParse(raw);
    if (env.success) throw new ApiError(res.status, env.data.code, env.data.message, env.data.law, env.data.details, env.data.requestId);
    throw new ApiError(res.status, res.status >= 500 ? 'INTERNAL' : 'BAD_REQUEST', `Request failed (HTTP ${res.status}).`);
  }
  if (res.status === 204) return undefined as TpResponse<K>;
  let json: unknown;
  try {
    json = await res.json();
  } catch (e) {
    console.warn('response is not JSON', e);
    throw new ApiError(res.status, 'BAD_RESPONSE', 'The server sent a response that is not JSON.');
  }
  const parsed = route.response.safeParse(json);
  if (!parsed.success) throw new ApiError(res.status, 'BAD_RESPONSE', `The server response for ${String(name)} did not match the contract.`);
  return parsed.data as TpResponse<K>;
}

/** Query keys every Today/Pipeline mutation invalidates, so all views stay in step. */
export const TP_KEYS = ['tp-board', 'tp-pipeline', 'tp-detail', 'relationship', 'relationships', 'today', 'log', 'commitments', 'drafts', 'memory'] as const;
